import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Observable, of, Subject } from "rxjs";
import { switchMap, tap } from "rxjs/operators";
import { HttpClientWrapper } from "./http-client.wrapper";
import { ApiRouterService } from "./api-router.service";
import {
  SampleBarGraphDataDTO,
  CacheBarGraphDTO,
  LineGraphDataDTO
} from "psa-tnt-models";

import { COLOR_PALETTE } from "../constants/chart-colors";
import { IGraph } from "../components/pages/analytics/graph-config";

export interface DatesParameter {
  dateMin: number;
  dateMax: number;
}

export type AnalyticsDates = {
  start: number;
  end: number;
};

export type AnalyticsForGraphics = {
  keys: string[];
  reference: string[];
  datas: any[][][];
  labels: string[];
};

export type AnalyticsFilters = {
  containers: string[];
  dates: AnalyticsDates;
  days?: number;
  isPolyvalent?: boolean;
};

export type AnalyticsBodyRequest = {
  containers?: string[];
  dates?: AnalyticsDates;
  hasOnlyRed?: boolean;
  xAxis?: string;
  days?: number;
  isPolyvalent?: boolean;
};

export type TokenMetadata = {
  access_token: string;
  token_type: string;
  expires_in: number;
  issued_at: string;
};

@Injectable()
export class AnalyticsControllerService {
  private analyticsRemainingTimeSubject: Subject<number | undefined> =
    new Subject();

  // JWT token cache
  private cachedToken: string | null = null;
  private tokenExpiresAt: number = 0;

  constructor(
    public httpClientWrapperHist: HttpClientWrapper<AnalyticsForGraphics>,
    public httpClientWrapperConfig: HttpClientWrapper<any>,
    private http: HttpClient,
    private apiRouterService: ApiRouterService
  ) {}

  private getJwtToken(): Observable<string> {
    if (this.cachedToken && Date.now() < this.tokenExpiresAt) {
      return of(this.cachedToken);
    }

    const tokenJWTUrl = `${this.apiRouterService.getNodeUrl()}jwt/token`;
    return this.httpClientWrapperConfig
      .postUnique(tokenJWTUrl, JSON.stringify({}))
      .pipe(
        tap((tokenResponse: TokenMetadata) => {
          this.cachedToken = tokenResponse.access_token;
          // Use expires_in with a 60s safety margin
          this.tokenExpiresAt =
            Date.now() + (tokenResponse.expires_in - 60) * 1000;
        }),
        switchMap((tokenResponse: TokenMetadata) =>
          of(tokenResponse.access_token)
        )
      );
  }

  public generateAnalyticsData(
    requestName: string,
    requestId: number,
    graphConfig: IGraph,
    filters: AnalyticsFilters
  ): Observable<AnalyticsForGraphics> {
    const data: AnalyticsBodyRequest = {
      containers: filters.containers
    };

    if (graphConfig.frontConfig?.dateRange) data.dates = filters.dates;
    if (graphConfig.frontConfig?.days) {
      data.days = filters.days;
    }
    if (filters.isPolyvalent !== undefined) {
      data.isPolyvalent = filters.isPolyvalent;
    }

    if (graphConfig.requestsParams) {
      data.hasOnlyRed = graphConfig.requestsParams[requestId].hasOnlyRed;
      data.xAxis = graphConfig.requestsParams[requestId].xAxis;
    }
    return this.getJwtToken().pipe(
      switchMap((token: string) => {
        const headers = new HttpHeaders({
          Authorization: `Bearer ${token}`
        });
        return this.http.post<AnalyticsForGraphics>(
          this.apiRouterService.getPythonUrl() +
            `analytics/data/${requestName}`,
          JSON.stringify(data),
          { headers }
        );
      })
    );
  }

  public getRemainingTime(): Subject<number | undefined> {
    return this.analyticsRemainingTimeSubject;
  }

  public setRemainingTime(remainingTime: number | undefined) {
    this.analyticsRemainingTimeSubject.next(remainingTime);
  }

  private getRoundedZoomedYmax(numberArray: number[]): number {
    let max = Math.max(...numberArray);
    let upperFence = this.UpperFence(numberArray);
    return Math.round(
      upperFence > max ? max + max / 10 : upperFence + upperFence / 10
    );
  }

  private Quantile(data: number[], q: number) {
    data = this.ArraySortNumbers(data);
    var pos = (data.length - 1) * q;
    var base = Math.floor(pos);
    var rest = pos - base;
    if (data[base + 1] !== undefined) {
      return data[base] + rest * (data[base + 1] - data[base]);
    } else {
      return data[base];
    }
  }

  private UpperFence(data: number[]) {
    let q1 = this.Quantile(data, 0.25);
    let q3 = this.Quantile(data, 0.75);
    return q3 + 1.5 * (q3 - q1);
  }

  private ArraySortNumbers(inputarray: number[]): number[] {
    return inputarray.sort(function (a: number, b: number) {
      return a - b;
    });
  }

  private formatStorageData(
    data: SampleBarGraphDataDTO,
    graph: any,
    sites?: string[]
  ): any {
    let firstElement = true;
    let hoverTexts: string[] = [];
    const filteredData: SampleBarGraphDataDTO = {
      type: "bar",
      x: [],
      y: [],
      x_labels: [],
      samples: []
    };

    // Filter data based on sites or take first DISPLAY_NBR elements
    if (sites && sites.length > 0) {
      for (let i = 0; i < data.x.length; i++) {
        if (sites.includes(data.x[i])) {
          filteredData.x.push(data.x[i]);
          filteredData.y.push(data.y[i]);
          filteredData.x_labels.push(data.x_labels[i]);
          filteredData.samples.push(data.samples[i]);
        }
      }
    } else {
      filteredData.x = data.x;
      filteredData.y = data.y;
      filteredData.x_labels = data.x_labels;
      filteredData.samples = data.samples;
    }

    // Generate hover texts
    for (let i = 0; i < filteredData.x.length; i++) {
      hoverTexts.push(
        "<b> Site : </b>" +
          filteredData.x_labels[i] +
          "<br>" +
          "<b> Time : </b>" +
          convertFloatingDaysToTimeFormat(filteredData.y[i]) +
          "<br>" +
          "<b> Sample : </b>" +
          filteredData.samples[i]
      );
    }

    // Create paired data objects to maintain alignment when sorted
    let pairedData = filteredData.x.map((site, index) => ({
      site: site,
      value: filteredData.y[index],
      label: filteredData.x_labels[index],
      hoverText: hoverTexts[index]
    }));

    // Push data to graph with proper alignment
    graph.data.push({
      x: pairedData.map((item) => item.site),
      y: pairedData.map((item) => item.value),
      name: `Sites`,
      text: pairedData.map((item) => item.hoverText),
      hoverinfo: "text",
      textposition: "none",
      type: "bar",
      marker: {
        color: COLOR_PALETTE[0]
      }
    });

    // Adjust the y-axis range for the first element (if needed)
    if (firstElement) {
      firstElement = false;
      graph.layout.yaxis["range"] = [
        0,
        this.getRoundedZoomedYmax(filteredData.y)
      ];
    }

    return graph;
  }

  private formatTransitData(
    data: SampleBarGraphDataDTO,
    graph: any,
    departureSites?: string[],
    arrivalSites?: string[]
  ): any {
    let firstElement = true;
    let hoverTexts: string[] = [];
    const filteredData: SampleBarGraphDataDTO = {
      type: "bar",
      x: [],
      y: [],
      x_labels: [],
      samples: []
    };

    // Filter based on departure sites
    const isDepartureSiteFiltered = departureSites && departureSites.length > 0;
    const isArrivalSiteFiltered = arrivalSites && arrivalSites.length > 0;
    if (isDepartureSiteFiltered || isArrivalSiteFiltered) {
      for (let i = 0; i < data.x.length; i++) {
        const [departureSite, arrivalSite] = data.x[i].split("/");
        if (isDepartureSiteFiltered && isArrivalSiteFiltered) {
          if (
            departureSites.includes(departureSite) &&
            arrivalSites.includes(arrivalSite)
          ) {
            filteredData.x.push(data.x[i]);
            filteredData.y.push(data.y[i]);
            filteredData.x_labels.push(data.x_labels[i]);
            filteredData.samples.push(data.samples[i]);
          }
        } else if (isDepartureSiteFiltered) {
          if (departureSites.includes(departureSite)) {
            filteredData.x.push(data.x[i]);
            filteredData.y.push(data.y[i]);
            filteredData.x_labels.push(data.x_labels[i]);
            filteredData.samples.push(data.samples[i]);
          }
        } else if (isArrivalSiteFiltered) {
          if (arrivalSites.includes(arrivalSite)) {
            filteredData.x.push(data.x[i]);
            filteredData.y.push(data.y[i]);
            filteredData.x_labels.push(data.x_labels[i]);
            filteredData.samples.push(data.samples[i]);
          }
        }
      }
    }

    // Take all elements if no filters applied
    if (filteredData.x.length === 0) {
      filteredData.x = data.x;
      filteredData.y = data.y;
      filteredData.x_labels = data.x_labels;
      filteredData.samples = data.samples;
    }

    // Generate hover texts
    for (let i = 0; i < filteredData.x.length; i++) {
      hoverTexts.push(
        "<b> Site : </b>" +
          filteredData.x_labels[i] +
          "<br>" +
          "<b> Time : </b>" +
          convertFloatingHoursToTimeFormat(filteredData.y[i]) +
          "<br>" +
          "<b> Sample : </b>" +
          filteredData.samples[i]
      );
    }

    // Create paired data to maintain alignment
    let pairedData = filteredData.x.map((route, index) => ({
      route: route,
      value: filteredData.y[index],
      label: filteredData.x_labels[index],
      hoverText: hoverTexts[index]
    }));

    // Push data to graph with proper alignment
    graph.data.push({
      x: pairedData.map((item) => item.route),
      y: pairedData.map((item) => item.value),
      name: `Routes`,
      text: pairedData.map((item) => item.hoverText),
      hoverinfo: "text",
      textposition: "none",
      type: "bar",
      marker: {
        color: COLOR_PALETTE[0]
      }
    });

    // Adjust the y-axis range
    if (firstElement) {
      firstElement = false;
      graph.layout.yaxis["range"] = [
        0,
        this.getRoundedZoomedYmax(filteredData.y)
      ];
    }

    return graph;
  }

  private formatStockLevelData(
    data: LineGraphDataDTO,
    graph: any,
    sites?: string[]
  ): any {
    let hoverTexts: string[] = [];
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      timeZone: "UTC",
      timeZoneName: "short"
    };
    for (const line of data.lines) {
      //? If sites are provided and the site is not in the list, skip it
      if (sites && sites.length && !sites.includes(line.site)) continue;
      hoverTexts = [];
      for (let j = 0; j < line.x.length; j++) {
        hoverTexts.push(
          "<b> Number on site : </b>" +
            line.y[j] +
            "<br><b> % on site : </b>" +
            Math.round(line.percent[j] * 10) / 10 +
            "%" +
            "<br><b> Total equiped : </b>" +
            data.total +
            "<br><b> date : </b>" +
            new Date(line.x[j]).toLocaleDateString(undefined, options) +
            "<br><b> Site : </b>" +
            line.site +
            "<br><b> Site label : </b>" +
            line.site_label
        );
      }
      graph.data.push({
        y: line.y,
        x: line.x,
        name: `${line.site}`,
        text: hoverTexts,
        hoverinfo: "text",
        type: "scatter",
        marker: {
          color: COLOR_PALETTE[data.lines.indexOf(line) % COLOR_PALETTE.length]
        }
      });
    }
    return graph;
  }

  public formatRedOrangeData(
    data: CacheBarGraphDTO,
    graph: any,
    hasOnlyRed?: boolean,
    hasOnlyPolyvalent?: boolean,
    sites?: string[],
    packaging?: string[]
  ): any {
    // Create a working copy to avoid mutating the original data
    const dataWithDetails = data as CacheBarGraphDTO & {
      y_details?: Array<Array<{ pack: string; num: number }>>;
    };
    const workingData = {
      x: [...data.x],
      y: [...data.y],
      x_labels: [...data.x_labels],
      isRed: [...data.isRed],
      y_details: dataWithDetails.y_details
        ? dataWithDetails.y_details.map((details) => [...details])
        : []
    };

    // Apply packaging filter if specified (empty array = show all, same as no filter)
    if (packaging && packaging.length > 0 && workingData.y_details.length > 0) {
      for (let i = workingData.x.length - 1; i >= 0; i--) {
        const filteredDetails = (workingData.y_details[i] || []).filter(
          (detail) => packaging.includes(detail.pack)
        );

        if (!filteredDetails.length) {
          workingData.x.splice(i, 1);
          workingData.y.splice(i, 1);
          workingData.x_labels.splice(i, 1);
          workingData.isRed.splice(i, 1);
          workingData.y_details.splice(i, 1);
          continue;
        }

        workingData.y_details[i] = filteredDetails;
        workingData.y[i] = filteredDetails.reduce(
          (sum, detail) => sum + detail.num,
          0
        );
      }
    }

    // Apply sites filter if specified
    if (sites && sites.length > 0) {
      for (let i = workingData.x.length - 1; i >= 0; i--) {
        if (!sites.includes(workingData.x[i])) {
          workingData.x.splice(i, 1);
          workingData.y.splice(i, 1);
          workingData.x_labels.splice(i, 1);
          workingData.isRed.splice(i, 1);
          if (workingData.y_details.length > 0) {
            workingData.y_details.splice(i, 1);
          }
        }
      }
    }

    // Merge duplicate sites by summing their values
    const mergedSumMap = new Map<
      string,
      { value: number; label: string; isRed: boolean }
    >();
    const siteOrderMap = new Map<string, number>();

    for (let i = 0; i < workingData.x.length; i++) {
      // Calculate total sum per site for all cases (needed for stable sorting)
      if (siteOrderMap.has(workingData.x[i])) {
        const previousValue = siteOrderMap.get(workingData.x[i])!;
        siteOrderMap.set(workingData.x[i], previousValue + workingData.y[i]);
      } else {
        siteOrderMap.set(workingData.x[i], workingData.y[i]);
      }
      const key =
        workingData.x[i] + (workingData.isRed[i] ? "_red" : "_orange");
      if (mergedSumMap.has(key)) {
        const existing = mergedSumMap.get(key)!;
        mergedSumMap.set(key, {
          value: existing.value + workingData.y[i],
          label: existing.label,
          isRed: existing.isRed
        });
      } else {
        mergedSumMap.set(key, {
          value: workingData.y[i],
          label: workingData.x_labels[i],
          isRed: workingData.isRed[i]
        });
      }
    }

    // Convert map to arrays and separate red/orange data
    const redData: any[] = [];
    const orangeData: any[] = [];

    mergedSumMap.forEach((data, key) => {
      const site = key.split("_")[0]; // Extract site name from key
      const item = {
        site: site,
        value: data.value,
        hoverText: `<b>Site:</b> ${data.label}<br><b>Total Trackers:</b> ${data.value}`
      };

      if (data.isRed) {
        redData.push(item);
      } else {
        orangeData.push(item);
      }
    });

    // Add data based on hasOnlyRed filter
    if (hasOnlyRed === true) {
      if (redData.length > 0) {
        //! Order data by value (descending), then by site name (ascending) for stability
        redData.sort(
          (a, b) => b.value - a.value || a.site.localeCompare(b.site)
        );
        // Only show red data
        graph.data.push({
          x: redData.map((item) => item.site),
          y: redData.map((item) => item.value),
          name: "Alerts",
          text: redData.map((item) => item.hoverText),
          hoverinfo: "text",
          textposition: "none",
          type: "bar",
          marker: { color: COLOR_PALETTE[1] }
        });
      } else {
        // No red data available after filtering - add empty dataset to prevent errors
        graph.data.push({
          x: [],
          y: []
        });
      }
    } else if (hasOnlyRed === false) {
      if (orangeData.length > 0) {
        //! Order data by value (descending), then by site name (ascending) for stability
        orangeData.sort(
          (a, b) => b.value - a.value || a.site.localeCompare(b.site)
        );
        // Only show orange data
        graph.data.push({
          x: orangeData.map((item) => item.site),
          y: orangeData.map((item) => item.value),
          name: "Warnings",
          text: orangeData.map((item) => item.hoverText),
          hoverinfo: "text",
          textposition: "none",
          type: "bar",
          marker: { color: COLOR_PALETTE[2] }
        });
      } else {
        // No orange data available after filtering - add empty dataset to prevent errors
        graph.data.push({
          x: [],
          y: []
        });
      }
    } else if (hasOnlyRed === undefined) {
      const orderedSites = Array.from(siteOrderMap.entries())
        .sort((a, b) => b[1] - a[1])
        .map(([site]) => site);
      const orderedSiteIndexes = new Map(
        orderedSites.map((site, index) => [site, index])
      );

      for (const site of orderedSites) {
        // If sites filter is active, skip sites not in the filter
        if (sites && sites.length > 0 && !sites.includes(site)) {
          continue;
        }

        // Ensure both red and orange data are sorted by site
        if (!redData.some((item) => item.site === site)) {
          redData.push({
            site,
            value: 0,
            hoverText: `<b>Site:</b> ${site}<br><b>Total Trackers:</b> 0`
          });
        }
        if (!orangeData.some((item) => item.site === site)) {
          orangeData.push({
            site,
            value: 0,
            hoverText: `<b>Site:</b> ${site}<br><b>Total Trackers:</b> 0`
          });
        }
      }

      redData.sort(
        (a, b) =>
          (orderedSiteIndexes.get(a.site) || 0) -
          (orderedSiteIndexes.get(b.site) || 0)
      );
      orangeData.sort(
        (a, b) =>
          (orderedSiteIndexes.get(a.site) || 0) -
          (orderedSiteIndexes.get(b.site) || 0)
      );
      // Show both as stacked bars
      if (orangeData.length > 0) {
        graph.data.push({
          x: orangeData.map((item) => item.site),
          y: orangeData.map((item) => item.value),
          name: "Warnings",
          text: orangeData.map((item) => item.hoverText),
          hoverinfo: "text",
          textposition: "none",
          type: "bar",
          marker: { color: COLOR_PALETTE[2] }
        });
      }
      if (redData.length > 0) {
        graph.data.push({
          x: redData.map((item) => item.site),
          y: redData.map((item) => item.value),
          name: "Alerts",
          text: redData.map((item) => item.hoverText),
          hoverinfo: "text",
          textposition: "none",
          type: "bar",
          marker: { color: COLOR_PALETTE[1] }
        });
      }
    }

    return graph;
  }

  public formatGraphData(
    graphRequest: string,
    data: any,
    graph: any,
    filters?: any
  ): any {
    switch (graphRequest) {
      case "storage":
        return this.formatStorageData(data, graph, filters?.sites);
      case "transit":
        return this.formatTransitData(
          data,
          graph,
          filters?.departureSites,
          filters?.arrivalSites
        );
      case "stockLevel":
        return this.formatStockLevelData(data, graph, filters?.sites);
      case "cacheGraphs":
        return this.formatRedOrangeData(
          data,
          graph,
          filters?.hasOnlyRed,
          filters?.hasOnlyPolyvalent,
          filters?.sites,
          filters?.packaging
        );
      default:
        console.log("default switch analytics");
        return;
    }
  }
}

function convertFloatingHoursToTimeFormat(floatinglHours: number): string {
  const hours = Math.floor(floatinglHours);
  const minutes = Math.round((floatinglHours - hours) * 60);
  return `${hours.toString().padStart(2, "0")}h${minutes.toString().padStart(2, "0")}min`;
}

// returns Ddays HHh MMmin
function convertFloatingDaysToTimeFormat(floatingDays: number): string {
  const days = Math.floor(floatingDays);
  const hours = Math.floor((floatingDays - days) * 24);
  const minutes = Math.round(((floatingDays - days) * 24 - hours) * 60);
  return `${days.toString()}${days > 1 ? " days" : " day"} ${hours
    .toString()
    .padStart(2, "0")}h${minutes.toString().padStart(2, "0")}min`;
}
